import React from 'react';
import { Check, Download } from 'lucide-react';

/**
 * Renders 1–4 image variants from a single generation call.
 *
 * Variants live in transient parent state and are only persisted when the user
 * takes a "next action" (regenerate, continue-from-here, unmount). Clicking a
 * variant just highlights it via the parent's `pickedId`.
 *
 * Layout adapts to count:
 *   - 1 variant: single full-width card
 *   - 2 variants: 1×2
 *   - 3 variants: 1×3
 *   - 4 variants: 2×2
 */
const VariantGrid = ({ variants, pickedId, onPick, onDownload }) => {
  if (!variants || variants.length === 0) return null;

  const cols =
    variants.length === 1 ? 'grid-cols-1'
    : variants.length === 2 ? 'grid-cols-2'
    : variants.length === 3 ? 'grid-cols-1 sm:grid-cols-3'
    : 'grid-cols-2';

  return (
    <div className={`grid ${cols} gap-3`}>
      {variants.map((v, idx) => {
        const id = v.id ?? idx;
        const isPicked = pickedId === id;
        const src = `data:${v.mime_type || 'image/png'};base64,${v.image_base64}`;

        return (
          <div
            key={id}
            className={`relative group rounded-lg overflow-hidden border-2 transition-all cursor-pointer
                       bg-white dark:bg-gray-900
                       ${isPicked
                          ? 'border-blue-500 shadow-lg ring-2 ring-blue-500/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'}`}
            onClick={() => onPick(id)}
          >
            <img src={src} alt={`Variant ${idx + 1}`} className="w-full h-auto block" />

            {/* Variant number badge */}
            {variants.length > 1 && (
              <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium bg-black/60 text-white">
                {idx + 1}
              </span>
            )}

            {/* Picked badge */}
            {isPicked && (
              <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500 text-white shadow">
                <Check className="w-3 h-3" />
                Picked
              </span>
            )}

            {/* Download (per-variant) — only the picked one will persist to history */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDownload?.(v, idx); }}
              className="absolute bottom-2 right-2 p-1.5 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-black/80 transition-opacity"
              title="Download this variant"
            >
              <Download className="w-3.5 h-3.5" />
            </button>

            {/* Pick hint on hover when not picked */}
            {!isPicked && variants.length > 1 && (
              <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/70 to-transparent text-xs font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity">
                Click to pick
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default VariantGrid;
